import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Helmet from 'react-helmet';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import Header from '../../components/ui/Header';
import PricingCard from './components/PricingCard';
import SubscriptionStatus from './components/SubscriptionStatus';
import PaymentMethodCard from './components/PaymentMethodCard';
import BillingHistory from './components/BillingHistory';
import TestimonialSection from './components/TestimonialSection';
import CheckoutForm from './components/CheckoutForm';
import CreditPackPurchase from '../../components/CreditPackPurchase';
import HybridCreditBalance from '../../components/HybridCreditBalance';
import CreditManagement from '../../components/CreditManagement';
import AdSense from '../../components/AdSense';
import HybridCreditService from '../../services/hybridCreditService';
import { useAuth } from '../../contexts/AuthContext';
import { stripeService } from '../../services/stripeService';
import Footer from '../../components/Footer';

const SubscriptionManagement = () => {
  const location = useLocation();
  const { user, userProfile, isAuthenticated, isPremium } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showCheckout, setShowCheckout] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [userSubscription, setUserSubscription] = useState(null);
  const [error, setError] = useState(null);
  const [upgradeMessage, setUpgradeMessage] = useState(null);
  const [creditBalance, setCreditBalance] = useState(0);
  const [showCreditPurchase, setShowCreditPurchase] = useState(false);

  // Handle upgrade message from location state
  useEffect(() => {
    if (location?.state?.message) {
      setUpgradeMessage(location.state.message);
      // Clear the message from location state
      window.history.replaceState({}, document.title);
    }
  }, [location?.state]);

  // Load subscription data
  useEffect(() => {
    const loadSubscriptionData = async () => {
      setIsLoading(true);
      setError(null);

      try {
        // Load subscription plans
        const { data: plans, error: plansError } = await stripeService?.getSubscriptionPlans();
        if (plansError) {
          console.error('Failed to load subscription plans:', plansError);
          setError('Failed to load subscription plans');
        } else {
          setSubscriptionPlans(plans || []);
        }

        // Load user subscription if authenticated
        if (isAuthenticated && user?.id) {
          const { data: subscription, error: subError } = await stripeService?.getUserSubscription(user?.id);
          if (!subError) {
            setUserSubscription(subscription);
          }
        }
      } catch (err) {
        console.error('Error loading subscription data:', err);
        setError('Failed to load subscription data');
      } finally {
        setIsLoading(false);
      }
    };

    loadSubscriptionData();
  }, [isAuthenticated, user?.id]);

  // Convert subscription plans for display
  const pricingPlans = subscriptionPlans?.map(plan => ({
    id: plan?.tier,
    name: plan?.tier === 'free' ? 'Free Account' : plan?.tier === 'premium' ? 'Premium Account — $19.99/month' : plan?.name,
    price: plan?.tier === 'premium' ? 19.99 : plan?.amount_cents / 100, // Force $19.99 for premium
    period: plan?.billing_interval,
    stripe_price_id: plan?.stripe_price_id,
    amount_cents: plan?.tier === 'premium' ? 1999 : plan?.amount_cents, // Force 1999 cents for premium
    description: plan?.tier === 'free' ? 'Perfect for getting started with career development' : 'Unlock your full career potential with advanced features',
    features: plan?.tier === 'free' ? [
          // Free Account Features (✅ included)
          { text: '1 Career Assessment', included: true },
          { text: '1 Saved Plan', included: true },
          { text: '2 Exercises per Week', included: true },
          { text: 'AI Plan', included: true },
          // Free Account Limitations (❌ not included)
          { text: '3 Career Assessments per month (included)', included: false },
          { text: 'Unlimited Saved Plans', included: false },
          { text: 'Unlimited Exercises', included: false },
          { text: 'Resume Builder with Skills & Experience Suggestions', included: false },
          { text: 'Salary Insights by ZIP Code & Occupation', included: false },
          { text: 'Personalized Development Plans', included: false },
          { text: 'Export Capabilities (PDF, Word, Shareable Links)', included: false },
          { text: 'Access to All 900+ Occupations', included: false },
          { text: 'Priority Email Support', included: false }
        ] : [
          // Premium Account Features (✅ all included)
          { text: '3 Career Assessments per month (included)', included: true },
          { text: 'Unlimited Saved Plans', included: true },
          { text: 'Unlimited Exercises', included: true },
          { text: 'AI Plan', included: true },
          { text: 'Resume Builder with Skills & Experience Suggestions', included: true },
          { text: 'Salary Insights by ZIP Code & Occupation', included: true },
          { text: 'Personalized Development Plans', included: true },
          { text: 'Export Capabilities (PDF, Word, Shareable Links)', included: true },
          { text: 'Access to All 900+ Occupations', included: true },
          { text: 'Priority Email Support', included: true }
        ],
    buttonText: (plan?.tier === 'free' && !isPremium) || (plan?.tier === 'premium' && isPremium)
      ? 'Current Plan' 
      : plan?.tier === 'premium' ? 'Upgrade to Premium' : 'Downgrade',
    buttonVariant: (plan?.tier === 'free' && !isPremium) || (plan?.tier === 'premium' && isPremium)
      ? 'outline' : 'default'
  }));

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'BarChart3' },
    { id: 'billing', label: 'Billing', icon: 'CreditCard' },
    { id: 'credits', label: 'Credits', icon: 'Coins' },
    { id: 'history', label: 'History', icon: 'Receipt' }
  ];

  const handleAuthRequired = () => {
    // In real app, this would redirect to authentication
    console.log('Authentication required');
  };

  const handleCreditPurchaseSuccess = async (pack, paymentIntent) => {
    console.log('Credit purchase successful:', pack, paymentIntent);
    
    try {
      // Actually add credits to the database
      // Add purchased credits (expire in 3 months)
      const expiresAt = new Date();
      expiresAt.setUTCMonth(expiresAt.getUTCMonth() + 3);
      
      const { success, error } = await HybridCreditService.addCredits(
        user?.id,
        'purchase',
        pack.credits,
        expiresAt.toISOString(),
        { pack_name: pack.name, purchase_simulation: true }
      );
      
      if (success) {
        console.log(`Successfully added ${pack.credits} credits to user account`);
        // Refresh credit balance
        setCreditBalance(prev => prev + pack.credits);
        // Show success message
        setUpgradeMessage(`Successfully purchased ${pack.name}! ${pack.credits} credits added to your account.`);
      } else {
        console.error('Failed to add credits to database:', error);
        setError('Purchase successful but failed to add credits. Please contact support.');
      }
    } catch (err) {
      console.error('Error adding credits:', err);
      setError('Purchase successful but failed to add credits. Please contact support.');
    }
  };

  const handleCreditPurchaseError = (error) => {
    console.error('Credit purchase failed:', error);
    setError('Credit purchase failed. Please try again.');
  };

  const handleCreditRefresh = () => {
    // This will be called by CreditBalance component
    console.log('Refreshing credit balance...');
  };

  const handleLogout = () => {
    // Handle logout logic
    console.log('Logout requested');
  };

  const handleSelectPlan = (plan) => {
    if (plan?.id === 'premium' && !isPremium) {
      // Always use the real Stripe price ID, regardless of what's in the database
      const fullPlan = {
        ...plan,
        stripe_price_id: 'price_1S5TCNE6oTidvpnUyJC5xrAe', // Always use the real Stripe price ID
        // Override pricing for premium
        price: 19.99,
        amount_cents: 1999
      };
      setSelectedPlan(fullPlan);
      setShowCheckout(true);
    }
  };

  const handlePaymentSuccess = (paymentData) => {
    // Refresh subscription data
    setShowCheckout(false);
    setSelectedPlan(null);
    setActiveTab('overview');
    
    // Show success message
    console.log('Payment successful:', paymentData);
    
    // Reload subscription data
    window.location?.reload(); // Simple refresh for now
  };

  const handleCancelSubscription = async () => {
    if (!userSubscription?.stripe_subscription_id) return;

    try {
      const { error } = await stripeService?.cancelSubscription(userSubscription?.stripe_subscription_id);
      if (error) {
        console.error('Failed to cancel subscription:', error);
        setError('Failed to cancel subscription');
      } else {
        // Refresh data
        window.location?.reload();
      }
    } catch (err) {
      console.error('Cancel subscription error:', err);
      setError('Failed to cancel subscription');
    }
  };

  const handleReactivateSubscription = () => {
    // In real app, this would call API to reactivate subscription
    console.log('Subscription reactivated');
  };

  const handleUpdatePaymentMethod = () => {
    // In real app, this would open payment method update flow
    console.log('Update payment method');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header user={user} onAuthRequired={handleAuthRequired} onLogout={handleLogout} />
        <div className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center space-y-4">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
            <p className="text-muted-foreground">Loading subscription details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (showCheckout) {
    return (
      <div className="min-h-screen bg-background">
        <Header user={user} onAuthRequired={handleAuthRequired} onLogout={handleLogout} />
        <div className="pt-16 min-h-screen">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="mb-6">
              <Button
                variant="ghost"
                onClick={() => setShowCheckout(false)}
                iconName="ArrowLeft"
                iconPosition="left"
              >
                Back to Plans
              </Button>
            </div>
            
            <CheckoutForm
              selectedPlan={selectedPlan}
              onPaymentSuccess={handlePaymentSuccess}
              onCancel={() => setShowCheckout(false)}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Subscription Management - CareerLift</title>
        <meta name="description" content="Manage your CareerLift subscription, view billing history, and upgrade to premium features for enhanced career development tools." />
      </Helmet>

      <div className="min-h-screen bg-background flex flex-col">
        <Header user={user} onAuthRequired={handleAuthRequired} onLogout={handleLogout} />
        
        <main className="flex-1 pt-32 pb-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            {/* Page Header */}
            <div className="text-center mb-2">
              <div className="flex items-center justify-center space-x-2 mb-4">
                <Icon name="Crown" size={32} className="text-primary" />
                <h1 className="text-3xl font-bold text-foreground">
                  Subscription Management
                </h1>
              </div>
              <div className="flex justify-center mb-2">
                <audio
                  src="/assets/audio/Subscription.mp3"
                  controls
                  preload="metadata"
                  className="h-8 max-w-xs"
                />
              </div>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-4">
                Manage your CareerLift subscription, billing preferences, and credit purchases
              </p>
              <div className="flex items-center justify-center space-x-4">
                <Badge variant={isPremium ? 'default' : 'secondary'}>
                  Plan: {isPremium ? 'Premium' : 'Free'}
                </Badge>
                <Badge variant="outline">
                  {isPremium ? 'Active Subscription' : 'Upgrade Available'}
                </Badge>
              </div>
            </div>

            {/* AdSense Banner */}
            <div className="mb-2">
              <AdSense adSlot="1234567890" />
            </div>

            {/* Upgrade Message */}
            {upgradeMessage && (
              <div className="mb-6 bg-warning/10 border border-warning/20 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <Icon name="AlertTriangle" size={20} className="text-warning mt-0.5" />
                  <div className="flex-1">
                    <h3 className="text-sm font-medium text-warning mb-1">
                      Assessment Limit Reached
                    </h3>
                    <p className="text-sm text-warning/80">
                      {upgradeMessage}
                    </p>
                  </div>
                  <button
                    onClick={() => setUpgradeMessage(null)}
                    className="text-warning/60 hover:text-warning transition-colors"
                  >
                    <Icon name="X" size={16} />
                  </button>
                </div>
              </div>
            )}

            {/* Error Display */}
            {error && (
              <div className="mb-6 bg-destructive/10 border border-destructive/20 rounded-lg p-4">
                <div className="flex items-center space-x-2">
                  <Icon name="AlertCircle" size={16} className="text-destructive" />
                  <span className="text-sm text-destructive">{error}</span>
                </div>
              </div>
            )}

            {/* Quick Stats for Premium Users */}
            {isPremium && userSubscription && (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-2">
                <div className="bg-card rounded-lg border border-border p-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-success/10 rounded-lg flex items-center justify-center">
                      <Icon name="CheckCircle" size={20} className="text-success" />
                    </div>
                    <div>
                      <div className="text-sm text-muted-foreground">Status</div>
                      <div className="font-semibold text-foreground capitalize">{userSubscription?.status || 'Active'}</div>
                    </div>
                  </div>
                </div>

                <div className="bg-card rounded-lg border border-border p-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                      <Icon name="Calendar" size={20} className="text-primary" />
                    </div>
                    <div>
                      <div className="text-sm text-muted-foreground">Next Billing</div>
                      <div className="font-semibold text-foreground">
                        {userSubscription?.current_period_end 
                          ? new Date(userSubscription.current_period_end)?.toLocaleDateString()
                          : 'N/A'}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-card rounded-lg border border-border p-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-warning/10 rounded-lg flex items-center justify-center">
                      <Icon name="DollarSign" size={20} className="text-warning" />
                    </div>
                    <div>
                      <div className="text-sm text-muted-foreground">Monthly Cost</div>
                      <div className="font-semibold text-foreground">
                        {userSubscription?.plan?.amount_cents 
                          ? stripeService?.formatAmount(userSubscription?.plan?.amount_cents === 2999 ? 1999 : userSubscription?.plan?.amount_cents)
                          : '$19.99'}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-card rounded-lg border border-border p-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-secondary/10 rounded-lg flex items-center justify-center">
                      <Icon name="TrendingUp" size={20} className="text-secondary" />
                    </div>
                    <div>
                      <div className="text-sm text-muted-foreground">Member Since</div>
                      <div className="font-semibold text-foreground">
                        {userSubscription?.created_at 
                          ? new Date(userSubscription.created_at)?.toLocaleDateString()
                          : 'Aug 2024'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Tab Navigation */}
            <div className="border-b border-border mb-2">
              <nav className="flex space-x-8">
                {tabs?.map((tab) => (
                  <button
                    key={tab?.id}
                    onClick={() => setActiveTab(tab?.id)}
                    className={`
                      flex items-center space-x-2 py-1 px-1 border-b-2 font-medium text-sm transition-colors duration-200
                      ${activeTab === tab?.id
                        ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted'
                      }
                    `}
                  >
                    <Icon name={tab?.icon} size={16} />
                    <span>{tab?.label}</span>
                  </button>
                ))}
              </nav>
            </div>

            {/* Tab Content */}
            <div className="space-y-8">
              {activeTab === 'overview' && (
                <>
                  {/* Current Subscription Status */}
                  <SubscriptionStatus
                    user={user}
                    userProfile={userProfile}
                    userSubscription={userSubscription}
                    isPremium={isPremium}
                    onCancelSubscription={handleCancelSubscription}
                    onReactivateSubscription={handleReactivateSubscription}
                  />

                  {/* Pricing Plans - hide for premium users */}
                  {!isPremium && (
                    <div>
                      <div className="text-center mb-2">
                        <h2 className="text-2xl font-bold text-foreground mb-2">
                          {isPremium ? 'Subscription Plans' : 'Upgrade to Premium'}
                        </h2>
                        <p className="text-muted-foreground max-w-2xl mx-auto">
                          {isPremium 
                            ? 'Manage your subscription or explore other available plans.' 
                            : 'Unlock advanced career insights, AI-powered recommendations, and personalized development plans to accelerate your professional growth.'}
                        </p>
                      </div>

                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-4xl mx-auto">
                        {pricingPlans?.map((plan) => (
                          <PricingCard
                            key={plan?.id}
                            plan={plan}
                            isCurrentPlan={
                              (plan?.id === 'free' && !isPremium) ||
                              (plan?.id === 'premium' && isPremium)
                            }
                            isPopular={plan?.id === 'premium' && !isPremium}
                            onSelectPlan={handleSelectPlan}
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Testimonials and Trust Signals */}
                  <TestimonialSection />
                </>
              )}

              {activeTab === 'billing' && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  <SubscriptionStatus
                    user={user}
                    userProfile={userProfile}
                    userSubscription={userSubscription}
                    isPremium={isPremium}
                    onCancelSubscription={handleCancelSubscription}
                    onReactivateSubscription={handleReactivateSubscription}
                  />
                  <PaymentMethodCard
                    user={user}
                    onUpdatePaymentMethod={handleUpdatePaymentMethod}
                  />
                </div>
              )}

              {activeTab === 'credits' && (
                <div className="space-y-8">
                  <CreditManagement 
                    userId={user?.id} 
                    isPremium={isPremium}
                  />

                  <CreditPackPurchase
                    userId={user?.id}
                    onPurchaseSuccess={handleCreditPurchaseSuccess}
                    onPurchaseError={handleCreditPurchaseError}
                  />

                  {!isPremium && (
                    <div className="bg-warning/10 border border-warning/20 rounded-xl p-6">
                      <div className="flex items-start space-x-3">
                        <Icon name="Crown" size={20} className="text-warning mt-0.5" />
                        <div>
                          <h4 className="font-semibold text-warning mb-2">Upgrade to Premium for Better Value</h4>
                          <p className="text-warning/80 text-sm mb-3">
                            Premium subscribers get 3 credits included monthly plus unlimited regenerations for just $19.99/month.
                          </p>
                          <Button 
                            onClick={() => setActiveTab('overview')}
                            variant="outline"
                            size="sm"
                            className="border-warning text-warning hover:bg-warning hover:text-warning-foreground"
                          >
                            <Icon name="Crown" size={14} className="mr-2" />
                            View Premium Plans
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'history' && (
                <BillingHistory user={user} />
              )}
            </div>

            {/* Help Section */}
            <div className="mt-12 bg-muted/30 rounded-lg p-6">
              <div className="text-center">
                <Icon name="HelpCircle" size={32} className="text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-foreground mb-2">Need Help?</h3>
                <p className="text-muted-foreground mb-4">
                  Have questions about your subscription or billing? We're here to help.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Button variant="outline" iconName="Mail" iconPosition="left">
                    Contact Support
                  </Button>
                  <Button variant="ghost" iconName="FileText" iconPosition="left">
                    View FAQ
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </main>

        {/* Bottom AdSense Banner */}
        <div className="mb-2">
          <AdSense adSlot="3456789012" />
        </div>

        {/* Footer */}
        <Footer />
      </div>
    </>
  );
};

export default SubscriptionManagement;